/**
 * InpaintTab Component
 *
 * Allows users to edit parts of images by painting a mask and describing what to generate.
 */

import { useReducer } from 'react';
import { Sparkles } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { ImageUploader } from '../ImageUploader';
import { ImageModelSelector } from '../ImageModelSelector';
import { StyleSelector } from '../StyleSelector';
import { ImageResultDisplay } from '../ImageResultDisplay';
import InpaintCanvas from '../InpaintCanvas';
import { imageGenerationAPI } from '@/services/imageGenerationAPI';
import { inpaintReducer, initialState } from './inpaintReducer';

export function InpaintTab() {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(inpaintReducer, initialState);

  const {
    imageBase64,
    imageFile,
    maskBase64,
    prompt,
    negativePrompt,
    showNegativePrompt,
    selectedStyles,
    model,
    isProcessing,
    error,
    result,
  } = state;

  const handleImageSelect = (base64: string, file: File) => {
    dispatch({ type: 'SET_IMAGE', payload: { base64, file } });
  };

  const handleRemoveImage = () => {
    dispatch({ type: 'REMOVE_IMAGE' });
  };

  const handleMaskChange = (mask: string) => {
    dispatch({ type: 'SET_MASK', payload: mask });
  };

  const handleGenerate = async () => {
    if (!imageBase64) {
      dispatch({
        type: 'SET_ERROR',
        payload: t('image.upscaleVary.errors.noImage', {
          defaultValue: 'Please upload an image first',
        }),
      });
      return;
    }

    if (!maskBase64) {
      dispatch({
        type: 'SET_ERROR',
        payload: t('image.inpaint.noMaskWarning', {
          defaultValue: 'Please draw a mask on the image to indicate which areas to change',
        }),
      });
      return;
    }

    if (!prompt.trim()) {
      dispatch({
        type: 'SET_ERROR',
        payload: 'Please enter a prompt describing what you want in the masked area',
      });
      return;
    }

    dispatch({ type: 'SET_PROCESSING', payload: true });
    dispatch({ type: 'SET_ERROR', payload: null });
    dispatch({ type: 'SET_RESULT', payload: null });

    try {
      const response = await imageGenerationAPI.inpaintImage({
        image: imageBase64,
        mask: maskBase64,
        prompt: prompt.trim(),
        negative_prompt: negativePrompt.trim() || undefined,
        styles: selectedStyles.length > 0 ? selectedStyles : undefined,
        async_process: false,
        model,
      });

      if (response.success && response.data) {
        // Defensive: ensure images is always an array
        const images = Array.isArray(response.data.images) ? response.data.images : [];
        if (images.length > 0) {
          dispatch({
            type: 'SET_RESULT',
            payload: {
              images,
              processingTime: response.data.processing_time_seconds || 0,
            },
          });
        } else {
          dispatch({
            type: 'SET_ERROR',
            payload: t('image.upscaleVary.errors.noResult', {
              defaultValue: 'No images were generated',
            }),
          });
        }
      } else {
        dispatch({
          type: 'SET_ERROR',
          payload:
            response.error ||
            t('image.upscaleVary.errors.processingFailed', {
              defaultValue: 'Failed to process image',
            }),
        });
      }
    } catch {
      dispatch({
        type: 'SET_ERROR',
        payload: t('image.upscaleVary.errors.networkError', {
          defaultValue: 'Network error. Please try again.',
        }),
      });
    } finally {
      dispatch({ type: 'SET_PROCESSING', payload: false });
    }
  };

  const handleReset = () => {
    dispatch({ type: 'RESET_RESULT' });
  };

  const canGenerate = imageBase64 && maskBase64 && prompt.trim() && !isProcessing;

  // If we have results, show them
  if (result) {
    return (
      <ImageResultDisplay
        images={result.images}
        processingTime={result.processingTime}
        onReset={handleReset}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-100">
          {t('image.inpaint.title', {
            defaultValue: 'Inpaint',
          })}
        </h2>
        <p className="text-secondary-600 dark:text-secondary-400 mt-1">
          {t('image.inpaint.description', {
            defaultValue:
              'Edit parts of an image by painting a mask and describing what to generate',
          })}
        </p>
      </div>

      {/* Image Upload */}
      <div>
        <label className="form-label">
          {t('image.inpaint.uploadPrompt', {
            defaultValue: 'Upload an image to edit',
          })}
        </label>
        <ImageUploader
          onImageSelect={handleImageSelect}
          onRemove={handleRemoveImage}
          disabled={isProcessing}
          maxSizeMB={10}
          previewUrl={imageFile ? URL.createObjectURL(imageFile) : undefined}
        />
      </div>

      {/* Canvas for drawing mask */}
      {imageBase64 && (
        <InpaintCanvas
          imageUrl={imageBase64}
          onMaskChange={handleMaskChange}
          disabled={isProcessing}
          className="mb-4"
        />
      )}

      {/* Prompt */}
      <div>
        <label htmlFor="prompt" className="form-label">
          {t('image.inpaint.prompt', {
            defaultValue: 'What do you want in the masked area?',
          })}{' '}
          <span className="text-red-500">*</span>
        </label>
        <textarea
          id="prompt"
          value={prompt}
          onChange={(e) => dispatch({ type: 'SET_PROMPT', payload: e.target.value })}
          disabled={isProcessing}
          placeholder={t('image.inpaint.promptPlaceholder', {
            defaultValue: 'Describe what should appear in the painted areas...',
          })}
          rows={3}
          className="input resize-none"
        />
      </div>

      {/* Negative Prompt (Optional) */}
      <div>
        {showNegativePrompt ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="negative-prompt" className="form-label mb-0">
                {t('image.prompt.negative.label', {
                  defaultValue: 'Negative Prompt',
                })}{' '}
                <span className="text-white/40 font-normal">
                  ({t('common.optional', { defaultValue: 'optional' })})
                </span>
              </label>
              <button
                type="button"
                onClick={() => dispatch({ type: 'HIDE_NEGATIVE_PROMPT' })}
                className="text-sm text-white/60 hover:text-white"
              >
                {t('image.prompt.negative.hide', { defaultValue: 'Hide negative prompt' })}
              </button>
            </div>
            <p className="text-xs text-white/50 mb-2">
              {t('image.prompt.negative.hint', {
                defaultValue: 'Describe elements you want to exclude from the generated image',
              })}
            </p>
            <textarea
              id="negative-prompt"
              value={negativePrompt}
              onChange={(e) => dispatch({ type: 'SET_NEGATIVE_PROMPT', payload: e.target.value })}
              disabled={isProcessing}
              placeholder={t('image.prompt.negative.placeholder', {
                defaultValue: 'What to avoid in the image...',
              })}
              rows={2}
              className="input resize-none"
            />
          </>
        ) : (
          <button
            type="button"
            onClick={() => dispatch({ type: 'SHOW_NEGATIVE_PROMPT' })}
            className="text-sm text-primary-400 hover:text-primary-300"
          >
            + {t('image.prompt.negative.toggle', { defaultValue: 'Add negative prompt' })}
          </button>
        )}
      </div>

      {/* Optional Styles */}
      <StyleSelector
        selectedStyles={selectedStyles}
        onChange={(styles) => dispatch({ type: 'SET_SELECTED_STYLES', payload: styles })}
        disabled={isProcessing}
        maxStyles={5}
      />

      {/* Checkpoint Model Selector */}
      <ImageModelSelector
        value={model}
        onChange={(value) => dispatch({ type: 'SET_MODEL', payload: value })}
        provider="fooocus"
        disabled={isProcessing}
      />

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Generate Button */}
      <button
        type="button"
        onClick={handleGenerate}
        disabled={!canGenerate}
        className={`
          w-full py-3 px-6 rounded-lg font-medium transition-all
          flex items-center justify-center gap-2
          ${
            canGenerate
              ? 'bg-primary-600 text-white hover:bg-primary-700 active:bg-primary-800'
              : 'bg-secondary-700 text-white/40 cursor-not-allowed'
          }
        `}
      >
        {isProcessing ? (
          <>
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
            {t('image.inpaint.generating', {
              defaultValue: 'Generating...',
            })}
          </>
        ) : (
          <>
            <Sparkles className="h-5 w-5" />
            {t('image.inpaint.generate', {
              defaultValue: 'Generate',
            })}
          </>
        )}
      </button>
    </div>
  );
}

export default InpaintTab;
